import { takeLatest, takeEvery, call, put, all } from 'redux-saga/effects';
import {
  DISPLAY_FETCH_REQUEST,
  DISPLAY_SAVE_REQUEST,
  DISPLAY_DELETE_REQUEST,
  type DisplayFetchPayload,
  type DisplaySaveRequestPayload,
  type DisplayDeleteRequestPayload,
} from './displayTypes';
import {
  displayFetchSuccess,
  displayFetchFailure,
  displaySaveSuccess,
  displaySaveFailure,
  displayItemDelete,
} from './displayActions';
import {
  createDisplayApi, updateDisplayApi, fetchDisplaysApi, deleteDisplayApi,
  type DisplaySavePayload, type DisplayApiItem, type DisplaySpotlightMediaApi, type DisplayTimeSlotApi,
  type DisplaySaveApiResponse, type DisplayDeleteApiResponse,
} from './displayApi';
import type { DisplayAttribute, DisplayItem, ScheduleSlot, SpotLightMedia } from '../../../components/display/data/types';

const WEEKDAY_TO_NUMERIC: Record<string, string> = {
  Monday: '1', Tuesday: '2', Wednesday: '3', Thursday: '4', Friday: '5', Saturday: '6', Sunday: '7',
};
const NUMERIC_TO_WEEKDAY: Record<string, string> = {
  '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', '4': 'Thursday', '5': 'Friday', '6': 'Saturday', '7': 'Sunday',
};

const toApiTimeSlot = (slot: ScheduleSlot): DisplayTimeSlotApi => ({
  weekday: WEEKDAY_TO_NUMERIC[slot.weekday] ?? slot.weekday,
  start_time: slot.start_time,
  end_time: slot.end_time,
});

const fromApiTimeSlot = (slot: DisplayTimeSlotApi): ScheduleSlot => ({
  weekday: NUMERIC_TO_WEEKDAY[slot.weekday] ?? slot.weekday,
  start_time: slot.start_time,
  end_time: slot.end_time,
});

const mapSpotlightMedia = (media: DisplaySpotlightMediaApi): SpotLightMedia => ({
  name: media.name,
  mediaId: media.mediaId,
  mimeType: media.mimeType,
  orientation: media.orientation === 'PORTRAIT' ? 'Portrait' : 'Landscape',
  url: media.url,
  showTime: (media.showTime ?? []).map(fromApiTimeSlot),
  sequenceNo: media.sequenceNo,
  displayTime: media.displayTime,
});

/** Real backend → app DisplayItem. GET returns spotLightMedia/fallbackMedia/timeSlots nested under attribute;
 *  create/update responses carry the same shape in data.attribute. */
function mapDisplayItem(raw: DisplayApiItem): DisplayItem {
  const attr = raw.attribute ?? {};
  const attribute: DisplayAttribute = {
    mode: attr.mode ?? null,
    playlistId: attr.playlistId ?? null,
    orientation: attr.orientation === 'PORTRAIT' ? 'Portrait' : attr.orientation === 'LANDSCAPE' ? 'Landscape' : null,
    heartbeatInterval: attr.heartbeatInterval ?? null,
    timeSlots: (attr.timeSlots ?? []).map(fromApiTimeSlot),
    spotLightMedia: (attr.spotLightMedia ?? []).map(mapSpotlightMedia),
    fallbackMedia: attr.fallbackMedia ? mapSpotlightMedia(attr.fallbackMedia) : null,
  };
  return {
    id: raw.id,
    displayId: raw.id,
    templateId: raw.templateId,
    locationId: raw.locationId,
    secretKey: raw.secretKey,
    displayName: raw.displayName,
    name: raw.name,
    templateType: raw.templateType,
    isDeleted: raw.isDeleted,
    attribute,
    createdAt: raw.createdTime,
    updatedAt: raw.modifiedTime,
  };
}

function* fetchDisplaysSaga(action: { type: string; payload: DisplayFetchPayload }): Generator<any, void, any> {
  try {
    const res: { data: DisplayApiItem[] } = yield call(fetchDisplaysApi, action.payload.locationId, action.payload.search);
    const items = (Array.isArray(res.data) ? res.data : []).map(mapDisplayItem);
    yield put(displayFetchSuccess(items, action.payload.locationId));
  } catch (err: any) {
    yield put(displayFetchFailure(err?.message || 'Failed to load displays'));
  }
}

/** Real backend: POST /display/template (create) or POST /display/template/{id} (update). */
function* saveDisplaySaga(action: { type: string; payload: DisplaySaveRequestPayload }): Generator<any, void, any> {
  const { id, locationId, displayName, mode, mediaId, playlistId, fallbackMediaId, orientation, timeSlots, deferred } = action.payload;
  try {
    const apiOrientation = orientation === 'Portrait' ? 'PORTRAIT' : 'LANDSCAPE';
    const payload: DisplaySavePayload = {
      locationId,
      templateType: 'MEDIA',
      displayName,
      attribute: {
        mode,
        ...(mode === 'S' ? { mediaId } : { playlistId }),
        fallbackMediaId,
        orientation: apiOrientation,
        heartbeatInterval: null,
        timeSlots: timeSlots.map(toApiTimeSlot),
      },
    };

    const res: { data: DisplaySaveApiResponse } = id
      ? yield call(updateDisplayApi, id, payload)
      : yield call(createDisplayApi, payload);

    const item = mapDisplayItem(res.data.data);
    yield put(displaySaveSuccess(item));
    deferred.resolve(item);
  } catch (err: any) {
    const msg = err?.message || `Failed to ${id ? 'update' : 'create'} display`;
    yield put(displaySaveFailure(msg));
    deferred.reject(new Error(msg));
  }
}

/** Real backend: DELETE /display/{id}?locationId= — soft delete. */
function* deleteDisplaySaga(action: { type: string; payload: DisplayDeleteRequestPayload }): Generator<any, void, any> {
  const { id, locationId, deferred } = action.payload;
  try {
    const res: { data: { data: DisplayDeleteApiResponse } } = yield call(deleteDisplayApi, id, locationId);
    if (res.data?.data?.deleted === false) throw new Error('Display was not deleted');
    yield put(displayItemDelete(id));
    deferred.resolve();
  } catch (err: any) {
    deferred.reject(new Error(err?.message || 'Failed to delete display'));
  }
}

export function* displaySaga(): Generator<any, void, any> {
  yield all([
    takeLatest(DISPLAY_FETCH_REQUEST, fetchDisplaysSaga),
    // takeEvery — create/update/delete are independent user actions and shouldn't cancel one another.
    takeEvery(DISPLAY_SAVE_REQUEST, saveDisplaySaga),
    takeEvery(DISPLAY_DELETE_REQUEST, deleteDisplaySaga),
  ]);
}
